import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';

export interface HospitalizationFormValue {
  admitDate: string;
  dischargeDate: string;
  reason: string;
  facility: string;
  notes: string;
}

export const EMPTY_HOSPITALIZATION: HospitalizationFormValue = {
  admitDate: '',
  dischargeDate: '',
  reason: '',
  facility: '',
  notes: '',
};

/**
 * PAT-06: both dates are required and discharge can't be before admit (same-day is allowed).
 * Dates are YYYY-MM-DD, so string comparison is chronological.
 */
export function validateHospitalization(value: Pick<HospitalizationFormValue, 'admitDate' | 'dischargeDate'>): string | null {
  if (!value.admitDate || !value.dischargeDate) return 'Admit and discharge dates are required.';
  if (value.dischargeDate < value.admitDate) return 'The discharge date can’t be before the admit date.';
  return null;
}

export interface HospitalizationDialogProps {
  open: boolean;
  /** Existing row values when editing; null when adding */
  initial: HospitalizationFormValue | null;
  onCancel: () => void;
  /** Saves locally; the row reaches the server with the demographics save */
  onSave: (value: HospitalizationFormValue) => void;
}

const inputClass = 'mt-1 w-full rounded-md border border-slate-300 p-2 text-sm bg-white';

const HospitalizationDialog: React.FC<HospitalizationDialogProps> = ({ open, initial, onCancel, onSave }) => {
  const baseId = useId();
  const [value, setValue] = useState<HospitalizationFormValue>(EMPTY_HOSPITALIZATION);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setValue(initial ?? EMPTY_HOSPITALIZATION);
    setError(null);
  }, [open, initial]);

  const set = (field: keyof HospitalizationFormValue) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setValue((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const validation = validateHospitalization(value);
    if (validation) {
      setError(validation);
      return;
    }
    onSave({
      admitDate: value.admitDate,
      dischargeDate: value.dischargeDate,
      reason: value.reason.trim(),
      facility: value.facility.trim(),
      notes: value.notes.trim(),
    });
  };

  const id = (name: string) => `${baseId}-${name}`;

  return (
    <Dialog open={open} onClose={onCancel} labelledBy={id('title')}>
      <form onSubmit={handleSubmit} className="text-left" noValidate>
        <h3 id={id('title')} className="text-lg font-semibold text-slate-800 pr-6">
          {initial ? 'Edit hospitalization' : 'Add hospitalization'}
        </h3>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label htmlFor={id('admit')} className="block text-sm font-medium text-slate-700">
              Admit date <span className="text-red-600" aria-hidden="true">*</span>
            </label>
            <input id={id('admit')} type="date" value={value.admitDate} onChange={set('admitDate')} className={inputClass} />
          </div>
          <div>
            <label htmlFor={id('discharge')} className="block text-sm font-medium text-slate-700">
              Discharge date <span className="text-red-600" aria-hidden="true">*</span>
            </label>
            <input
              id={id('discharge')}
              type="date"
              value={value.dischargeDate}
              min={value.admitDate || undefined}
              onChange={set('dischargeDate')}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor={id('reason')} className="block text-sm font-medium text-slate-700">Reason</label>
            <input id={id('reason')} value={value.reason} onChange={set('reason')} className={inputClass} />
          </div>
          <div>
            <label htmlFor={id('facility')} className="block text-sm font-medium text-slate-700">Facility</label>
            <input id={id('facility')} value={value.facility} onChange={set('facility')} className={inputClass} />
          </div>
          <div className="md:col-span-2">
            <label htmlFor={id('notes')} className="block text-sm font-medium text-slate-700">Notes</label>
            <textarea id={id('notes')} value={value.notes} onChange={set('notes')} rows={4} className={inputClass} />
          </div>
        </div>
        {error && (
          <p role="alert" className="mt-3 text-sm text-red-700">
            {error}
          </p>
        )}
        <p className="mt-2 text-xs text-slate-500">Changes are saved with “Save Patient Data”.</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 rounded-md text-sm bg-slate-800 hover:bg-slate-700 text-white">
            {initial ? 'Update' : 'Add'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default HospitalizationDialog;
